import { useState, type KeyboardEvent } from 'react';
import { useLocation } from 'react-router';
import { ShieldBan } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatTimeAgo } from '@/utils/format-date';
import {
  ArtifactOverflowMenu,
  ArtifactRenameDialog,
  ArtifactDeleteDialog,
} from '@/components/artifacts/artifact-action-dialogs';
import {
  getFileIcon,
  getFileIconBackgroundColor,
} from '@/constants/node-styling';
import { ROUTES } from '@/constants/routes';
import { SURFACE_SEARCH_PARAM_KEYS } from '@/components/chat-workspace/workspace-url';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { useArtifactRole } from '@/components/artifacts/use-artifact-role';
import {
  useArtifactActions,
  listItemPrimaryToNodeFile,
} from '@/components/artifacts/use-artifact-actions';
import type { ArtifactListItem, ArtifactFile } from '@/types/artifacts';
import {
  artifactTypeFromFile,
  artifactTypeLabel,
} from '@/utils/artifact-type-label';
import { cn } from '@/lib/utils';

/**
 * Preview proportions from the design's 720x438 card, whose footer is 72.
 * A ratio rather than a height because the card is stretched far wider in a
 * chat turn than in the gallery grid.
 */
const GRID_PREVIEW_ASPECT = 'aspect-[720/366]';

interface ArtifactCardProps {
  artifact: ArtifactListItem;
  /** Resolved primary file (optional — for download). */
  primaryFile?: ArtifactFile;
  onShare?: (artifact: ArtifactListItem) => void;
  viewMode?: 'grid' | 'list';
  /**
   * When provided, clicking the card calls this instead of
   * navigating to the detail page. Used in the chat panel to open the
   * embedded right-side artifact viewer.
   */
  onOpen?: (artifact: ArtifactListItem) => void;
  /** When true, hides the overflow action menu (rename / download / etc). */
  hideMenu?: boolean;
  /** Shown beneath the card when this is a new revision of an earlier file. */
  revision?: { label: string; changeSummary?: string };
  /** Dims the card and explains, for a file that cannot be opened. */
  unavailableReason?: string;
}

export function ArtifactCard({
  artifact,
  primaryFile,
  onShare,
  viewMode = 'grid',
  onOpen,
  hideMenu = false,
  revision,
  unavailableReason,
}: ArtifactCardProps) {
  const navigate = useOrgNavigate();
  const { search } = useLocation();
  const { canRename, canShare, canDelete } = useArtifactRole(artifact);
  const actions = useArtifactActions(artifact);

  const [isMenuHovered, setIsMenuHovered] = useState(false);

  const clickableBorderClass = isMenuHovered
    ? 'border-border'
    : 'border-border hover:border-primary/50';

  // Icon and accent key off a file extension, so read them from the primary
  // file: a stored title like "Investment thesis" carries no type signal.
  const iconSource =
    primaryFile?.name ?? artifact.primary_file_name ?? artifact.title;
  const FileIcon = getFileIcon(iconSource);
  const accent = getFileIconBackgroundColor(iconSource);

  // `other` is the stored "unclassified" type, which promotion falls back to
  // when the file's MIME type was generic. The file itself knows better.
  const displayType =
    artifact.type === 'other'
      ? (artifactTypeFromFile(
          iconSource,
          primaryFile?.mime_type ?? artifact.primary_file_mime_type,
        ) ?? artifact.type)
      : artifact.type;

  const hasThumbnail =
    artifact.thumbnail_status === 'ready' && artifact.thumbnail_url;

  const updatedAgo = formatTimeAgo(artifact.updated_at);

  const primaryNodeFile = listItemPrimaryToNodeFile(artifact, primaryFile);

  const handleOpen = () => {
    if (onOpen) {
      onOpen(artifact);
      return;
    }
    // Carry user_role through router state: the list response has it but the
    // detail endpoint does not return it, so the viewer relies on this.
    // Strip the surface param keys before carrying the rest of the search
    // over: `workflow`/`gallery` are this gallery's own filter here, but the
    // artifact detail route reads those same keys as a layered surface --
    // uncleared, a workflow-filtered gallery would open showing that
    // workflow instead of the artifact just clicked.
    const detailParams = new URLSearchParams(search);
    SURFACE_SEARCH_PARAM_KEYS.forEach((key) => detailParams.delete(key));
    const detailSearch = detailParams.toString();
    navigate(
      `${ROUTES.ARTIFACT_DETAIL.replace(':artifactId', artifact.id)}${detailSearch ? `?${detailSearch}` : ''}`,
      { state: { user_role: artifact.user_role } },
    );
  };

  const handleOpenKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  const handleDownload = () => {
    void actions.handleDownload(primaryNodeFile);
  };

  const overflowMenuWrapper = hideMenu ? null : (
    <div
      onMouseEnter={() => setIsMenuHovered(true)}
      onMouseLeave={() => setIsMenuHovered(false)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <ArtifactOverflowMenu
        onDownload={handleDownload}
        isDownloading={actions.isDownloading}
        canRename={canRename}
        onRename={actions.openRenameDialog}
        canShare={canShare}
        onShare={() => onShare?.(artifact)}
        canDelete={canDelete}
        onDelete={() => actions.setDeleteDialogOpen(true)}
      />
    </div>
  );

  const card =
    viewMode === 'list' ? (
      <div
        title={unavailableReason}
        role='button'
        tabIndex={0}
        className={cn(
          'flex cursor-pointer items-center gap-4 rounded-lg border bg-card px-4 py-3 transition-[border-color,background-color] hover:bg-accent/50',
          clickableBorderClass,
          unavailableReason && 'opacity-70',
        )}
        onClick={handleOpen}
        onKeyDown={handleOpenKeyDown}
      >
        {/* Type icon */}
        <div
          className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border'
          style={{ backgroundColor: accent }}
        >
          <FileIcon className='size-5 text-foreground' />
        </div>

        {/* Title */}
        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-medium text-foreground'>
            {artifact.title}
          </p>
        </div>

        {artifact.client_data && (
          <div className='flex shrink-0 items-center gap-1 rounded border border-yellow-700/40 bg-black/80 px-2 py-0.5 text-xs font-medium text-yellow-500'>
            <ShieldBan className='h-3 w-3' />
            Client Data
          </div>
        )}

        {/* Type · time + overflow menu */}
        <div className='flex shrink-0 items-center gap-1'>
          <span className='whitespace-nowrap text-xs text-muted-foreground'>
            {artifactTypeLabel(displayType)} · Last updated {updatedAgo}
          </span>
          {overflowMenuWrapper}
        </div>
      </div>
    ) : (
      <div
        title={unavailableReason}
        role='button'
        tabIndex={0}
        className={cn(
          'group relative flex cursor-pointer flex-col overflow-clip rounded-xl border bg-card shadow-sm transition-[border-color,box-shadow] hover:shadow-md',
          clickableBorderClass,
          unavailableReason && 'opacity-70',
        )}
        onClick={handleOpen}
        onKeyDown={handleOpenKeyDown}
      >
        {/* Thumbnail / preview area */}
        <div
          className={cn(
            'relative w-full overflow-hidden bg-accent/30',
            GRID_PREVIEW_ASPECT,
          )}
        >
          {hasThumbnail ? (
            <img
              src={artifact.thumbnail_url!}
              alt={artifact.title}
              className='absolute inset-0 h-full w-full object-cover pointer-events-none'
            />
          ) : (
            <div
              className='flex h-full w-full items-center justify-center'
              style={{ backgroundColor: `${accent}20` }}
            >
              <FileIcon className='size-12 opacity-30' />
            </div>
          )}

          {/* Client Data badge — top-right overlay */}
          {artifact.client_data && (
            <div
              className='absolute right-2 top-2 flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-yellow-500 drop-shadow-sm'
              style={{ background: 'rgba(9,9,11,0.8)' }}
            >
              <ShieldBan className='size-3.5' />
              Client Data
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='flex items-center gap-3 rounded-md bg-accent p-3'>
          {/* Type icon badge */}
          <div
            className='flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-background drop-shadow-sm'
            style={{ backgroundColor: accent }}
          >
            <FileIcon className='size-6 text-white dark:text-foreground' />
          </div>

          {/* Title + meta (type · last updated) */}
          <div className='flex min-w-0 flex-1 flex-col gap-1'>
            <p className='truncate text-base text-foreground'>
              {artifact.title}
            </p>
            <div className='flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground'>
              <span className='shrink-0'>{artifactTypeLabel(displayType)}</span>
              <span className='size-1 shrink-0 rounded-full bg-muted-foreground/50' />
              <span className='shrink-0 whitespace-nowrap'>
                Last updated {updatedAgo}
              </span>
            </div>
          </div>

          {/* Overflow menu */}
          {overflowMenuWrapper}
        </div>
      </div>
    );

  return (
    <>
      {revision ? (
        <div className='flex min-w-0 flex-col gap-1.5'>
          {card}
          <div className='flex items-start gap-1.5 text-xs text-muted-foreground'>
            <Badge variant='secondary' className='shrink-0'>
              {revision.label}
            </Badge>
            {revision.changeSummary && (
              <span className='min-w-0 wrap-break-word pt-0.5'>
                {revision.changeSummary}
              </span>
            )}
          </div>
        </div>
      ) : (
        card
      )}

      <ArtifactRenameDialog
        open={actions.renameDialogOpen}
        onOpenChange={actions.setRenameDialogOpen}
        value={actions.renameValue}
        onChange={actions.setRenameValue}
        onConfirm={actions.handleRename}
        isPending={actions.renamePending}
      />

      <ArtifactDeleteDialog
        open={actions.deleteDialogOpen}
        onOpenChange={actions.setDeleteDialogOpen}
        title={artifact.title}
        onConfirm={() => actions.handleDelete()}
        isPending={actions.deletePending}
      />
    </>
  );
}

/** Placeholder at the card's own proportions, for a row still being fetched. */
export function ArtifactCardSkeleton({
  viewMode = 'grid',
}: {
  viewMode?: 'grid' | 'list';
}) {
  if (viewMode === 'list') {
    return (
      <div
        data-testid='artifact-card-skeleton'
        className='flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3'
      >
        <Skeleton className='size-10 shrink-0 rounded-lg' />
        <Skeleton className='h-4 w-1/3' />
      </div>
    );
  }

  return (
    <div
      data-testid='artifact-card-skeleton'
      className='flex w-full flex-col overflow-clip rounded-xl border border-border bg-card shadow-sm'
    >
      <Skeleton className={cn('w-full rounded-none', GRID_PREVIEW_ASPECT)} />
      <div className='flex items-center gap-3 bg-accent p-3'>
        <Skeleton className='size-12 shrink-0 rounded-lg' />
        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <Skeleton className='h-5 w-2/3' />
          <Skeleton className='h-3 w-1/2' />
        </div>
      </div>
    </div>
  );
}
